import { Link } from "react-router-dom";
import { Menu, X, Mountain, Heart, HandHelping, Target, ChevronDown, Users, Share2, LogIn, LayoutDashboard } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usePiAuth } from "../contexts/PiAuthContext";

// Dropdown component
const NavDropdown = ({ label, icon: Icon, items, isDark = true }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const baseText = isDark ? "text-white/90 hover:text-white" : "text-[#5C5A52] hover:text-[#1A1915]";
  const dropBg = isDark ? "bg-[#1A2F23] border-white/10" : "bg-white border-[#D1CFC5]";

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 ${baseText} transition-colors font-medium`}
        data-testid="nav-community-dropdown"
      >
        {Icon && <Icon className="w-4 h-4" />}
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`absolute top-full right-0 mt-2 w-56 ${dropBg} border-2 rounded-xl shadow-xl overflow-hidden z-50`}>
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 ${isDark ? "text-white/80 hover:bg-white/5 hover:text-white" : "text-[#5C5A52] hover:bg-[#F7F6F2] hover:text-[#1A1915]"} transition-colors`}
              data-testid={item.testId}
            >
              {item.icon && <item.icon className={`w-4 h-4 ${isDark ? "text-[#D4A017]" : "text-[#2C4A3B]"}`} />}
              <div>
                <p className="text-sm font-medium">{item.label}</p>
                {item.desc && <p className={`text-[11px] ${isDark ? "text-white/40" : "text-[#5C5A52]/60"}`}>{item.desc}</p>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const communityItems = [
  { to: "/volunteer", icon: HandHelping, label: "Volunteer", desc: "Lend a hand", testId: "nav-volunteer" },
  { to: "/marketplace", icon: Target, label: "Marketplace", desc: "Services & opportunities", testId: "nav-marketplace" },
  { to: "/donate", icon: Heart, label: "Donate", desc: "Support the community", testId: "nav-donate" },
];

const ShareButton = ({ isDark = true }) => {
  const [copied, setCopied] = useState(false);
  
  const handleShare = async () => {
    const shareData = {
      title: "ekvalleys.com - EK Valleys",
      text: "Check out ekvalleys.com — the East Kootenay community business directory!",
      url: window.location.origin,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (err) {
        if (err.name !== "AbortError") fallbackCopy();
      }
    } else { fallbackCopy(); }
  };
  
  const fallbackCopy = () => {
    navigator.clipboard.writeText(window.location.origin).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const textClass = isDark ? "text-[#D4A017] hover:text-[#D4A017]/80" : "text-[#2C4A3B] hover:text-[#2C4A3B]/80";
  
  return (
    <button
      onClick={handleShare}
      className={`flex items-center gap-1.5 ${textClass} transition-colors font-medium text-sm bg-transparent border-none p-0 cursor-pointer`}
      data-testid="nav-share-btn"
      title="Share ekvalleys.com"
    >
      <Share2 className="w-4 h-4" />
      <span className="hidden lg:inline">{copied ? "Copied!" : "Share"}</span>
    </button>
  );
};

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="absolute top-0 left-0 right-0 z-50 px-6 py-4 md:px-12">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-white" data-testid="nav-logo">
          <Mountain className="w-8 h-8" strokeWidth={1.5} />
          <span className="font-heading text-xl font-bold">ekvalleys</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-7">
          <Link to="/directory" className="text-white/90 hover:text-white transition-colors font-medium" data-testid="nav-directory">Directory</Link>
          <Link to="/blog" className="text-white/90 hover:text-white transition-colors font-medium" data-testid="nav-blog">News</Link>
          <Link to="/list-business" className="text-white/90 hover:text-white transition-colors font-medium" data-testid="nav-list-business">List Your Business</Link>
          <Link to="/about" className="text-white/90 hover:text-white transition-colors font-medium" data-testid="nav-about">About</Link>
          <NavDropdown label="Community" icon={Users} items={communityItems} isDark={true} />
          <ShareButton isDark={true} />
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-[#1A2F23] py-4 px-6">
          <div className="flex flex-col gap-3">
            <Link to="/directory" className="text-white/90 hover:text-white py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>Directory</Link>
            <Link to="/blog" className="text-white/90 hover:text-white py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>News</Link>
            <Link to="/list-business" className="text-white/90 hover:text-white py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>List Your Business</Link>
            <Link to="/about" className="text-white/90 hover:text-white py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>About</Link>
            <div className="border-t border-white/10 pt-3 mt-1">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Community</p>
              <Link to="/volunteer" className="flex items-center gap-2 text-white/90 hover:text-white py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>
                <HandHelping className="w-4 h-4 text-[#D4A017]" /> Volunteer
              </Link>
              <Link to="/marketplace" className="flex items-center gap-2 text-white/90 hover:text-white py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>
                <Target className="w-4 h-4 text-[#D4A017]" /> Marketplace
              </Link>
              <Link to="/donate" className="flex items-center gap-2 text-white/90 hover:text-white py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>
                <Heart className="w-4 h-4 text-[#D4A017]" /> Donate
              </Link>
              <div className="py-2">
                <ShareButton isDark={true} />
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export const NavbarLight = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const { piUsername } = usePiAuth();

  return (
    <nav className="bg-[#EFEDE5] border-b-2 border-[#D1CFC5] px-6 py-3 md:px-12">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-[#1A1915]" data-testid="nav-logo-light">
          <Mountain className="w-7 h-7" strokeWidth={1.5} />
          <span className="font-heading text-lg font-bold">ekvalleys</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-7">
          <Link to="/directory" className="text-[#5C5A52] hover:text-[#1A1915] transition-colors font-medium">Directory</Link>
          <Link to="/blog" className="text-[#5C5A52] hover:text-[#1A1915] transition-colors font-medium">News</Link>
          <Link to="/list-business" className="text-[#5C5A52] hover:text-[#1A1915] transition-colors font-medium">List Your Business</Link>
          <Link to="/about" className="text-[#5C5A52] hover:text-[#1A1915] transition-colors font-medium">About</Link>
          <NavDropdown label="Community" icon={Users} items={communityItems} isDark={false} />
          <ShareButton isDark={false} />
          {user ? (
            <Link to="/dashboard" className="flex items-center gap-1.5 bg-[#2C4A3B] text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-[#1A2F23] transition-colors" data-testid="nav-dashboard-link">
              <LayoutDashboard className="w-3.5 h-3.5" />{piUsername ? `@${piUsername}` : "Dashboard"}
            </Link>
          ) : (
            <Link to="/login" className="flex items-center gap-1.5 bg-[#2C4A3B] text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-[#1A2F23] transition-colors" data-testid="nav-login-link">
              <LogIn className="w-3.5 h-3.5" />Log In
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-[#1A1915] p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden pt-4 border-t border-[#D1CFC5] mt-4">
          <div className="flex flex-col gap-2">
            <Link to="/directory" className="text-[#5C5A52] hover:text-[#1A1915] py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>Directory</Link>
            <Link to="/blog" className="text-[#5C5A52] hover:text-[#1A1915] py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>News</Link>
            <Link to="/list-business" className="text-[#5C5A52] hover:text-[#1A1915] py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>List Your Business</Link>
            <Link to="/about" className="text-[#5C5A52] hover:text-[#1A1915] py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>About</Link>
            <div className="border-t border-[#D1CFC5] pt-3 mt-1">
              <p className="text-[#5C5A52]/50 text-xs uppercase tracking-wider mb-2">Community</p>
              <Link to="/volunteer" className="flex items-center gap-2 text-[#5C5A52] hover:text-[#1A1915] py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>
                <HandHelping className="w-4 h-4 text-[#2C4A3B]" /> Volunteer
              </Link>
              <Link to="/marketplace" className="flex items-center gap-2 text-[#5C5A52] hover:text-[#1A1915] py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>
                <Target className="w-4 h-4 text-[#2C4A3B]" /> Marketplace
              </Link>
              <Link to="/donate" className="flex items-center gap-2 text-[#5C5A52] hover:text-[#1A1915] py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>
                <Heart className="w-4 h-4 text-[#2C4A3B]" /> Donate
              </Link>
              <div className="py-2">
                <ShareButton isDark={false} />
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
